import React, { useState, useEffect, useCallback } from "react";
import { adminApi } from "../api";
import useUrlState from "../hooks/useUrlState";

/**
 * Admin "API" tab — venue-scoped API key management + request log
 * viewer for the public Venue API v1. Strictly admin-only — never
 * exposed to the client portal.
 */
const ALL_SCOPES = [
  { key: "join",   label: "Join (create new members)" },
  { key: "renew",  label: "Renew (renew existing members)" },
  { key: "passes", label: "Passes (issue / re-issue digital passes)" },
  { key: "read",   label: "Read (member lookup)" },
];

export default function ApiKeysTab({ venueId, venueSlug }) {
  const [section, setSection] = useUrlState("api", "keys"); // keys | logs | docs
  return (
    <div className="api-tab">
      <div className="config-layout">
        <div className="config-nav">
          <button className={`config-nav-item ${section === "keys" ? "active" : ""}`} onClick={() => setSection("keys")}>
            <span>API Keys</span>
          </button>
          <button className={`config-nav-item ${section === "logs" ? "active" : ""}`} onClick={() => setSection("logs")}>
            <span>Request Logs</span>
          </button>
          <button className={`config-nav-item ${section === "docs" ? "active" : ""}`} onClick={() => setSection("docs")}>
            <span>Docs</span>
          </button>
        </div>
        <div className="config-panel">
          {section === "keys" && <ApiKeysList venueId={venueId} venueSlug={venueSlug} />}
          {section === "logs" && <ApiLogsList venueId={venueId} />}
          {section === "docs" && <ApiDocsPanel venueId={venueId} venueSlug={venueSlug} />}
        </div>
      </div>
    </div>
  );
}

function ApiDocsPanel({ venueId, venueSlug }) {
  const [md, setMd] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => {
    adminApi.getApiDocs(venueId, venueSlug)
      .then((r) => setMd(r.markdown || ""))
      .catch((e) => setErr(e.message));
  }, [venueId, venueSlug]);
  if (err) return <div className="toast toast-error">{err}</div>;
  return (
    <div className="form-section">
      <pre style={{
        margin: 0, padding: "1rem 1.25rem",
        background: "var(--bg-elevated)", border: "1px solid var(--border)",
        borderRadius: 6, fontSize: "0.82rem", lineHeight: 1.55,
        whiteSpace: "pre-wrap", wordBreak: "break-word",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      }}>{md}</pre>
    </div>
  );
}

/* ── Keys list + create / revoke ─────────────────────── */
function ApiKeysList({ venueId, venueSlug }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newRaw, setNewRaw] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true); setErr("");
    try { const r = await adminApi.listApiKeys(venueId); setKeys(r.keys || []); }
    catch (e) { setErr(e.message); }
    setLoading(false);
  }, [venueId]);
  useEffect(() => { reload(); }, [reload]);

  async function revoke(id, name) {
    if (!window.confirm(`Revoke API key "${name}"? Any partner using it will start getting 401.`)) return;
    try { await adminApi.revokeApiKey(venueId, id); await reload(); }
    catch (e) { setErr(e.message); }
  }

  return (
    <div className="form-section">
      <div className="section-header-row">
        <div>
          <h2>API Keys</h2>
          <p className="section-desc">Bearer-token credentials for the public Venue API v1.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ New API key</button>
      </div>

      <div className="info-box" style={{ marginBottom: "1rem" }}>
        <span>
          Endpoint base:{" "}
          <code>{window.location.origin}/api/v1/venues/{venueSlug || ":slug"}/</code>
        </span>
      </div>

      {err && <div className="toast toast-error" style={{ marginBottom: "1rem" }}>{err}</div>}

      {loading ? <div style={{ color: "var(--text-muted)" }}>Loading…</div> : (
        keys.length === 0 ? (
          <div className="empty-state" style={{ padding: "2rem", textAlign: "center" }}>
            <p style={{ color: "var(--text-muted)" }}>No API keys yet — create one to get started.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table" style={{ fontSize: "0.85rem" }}>
              <thead>
                <tr><th>Name</th><th>Prefix</th><th>Scopes</th><th>Test-only</th><th>Last used</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id}>
                    <td style={{ fontWeight: 600 }}>{k.name}</td>
                    <td><code>{k.key_prefix}…</code></td>
                    <td>{(k.scopes || []).join(", ") || <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
                    <td>{k.test_only ? <span className="pill pill-warn">Yes</span> : <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
                    <td>{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : <span style={{ color: "var(--text-muted)" }}>never</span>}</td>
                    <td>
                      {k.revoked_at
                        ? <span className="pill pill-muted">Revoked</span>
                        : <span className="pill pill-ok">Active</span>}
                    </td>
                    <td>
                      {!k.revoked_at && <button className="btn btn-outline btn-sm" onClick={() => revoke(k.id, k.name)}>Revoke</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {showCreate && (
        <CreateKeyModal
          venueId={venueId}
          onClose={() => { setShowCreate(false); reload(); }}
          onCreated={(rawKey) => setNewRaw(rawKey)}
        />
      )}

      {newRaw && <ShowRawKeyModal rawKey={newRaw} onClose={() => setNewRaw(null)} />}
    </div>
  );
}

function CreateKeyModal({ venueId, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState({ join: false, renew: false, passes: false, read: false });
  const [testOnly, setTestOnly] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await adminApi.createApiKey(venueId, {
        name,
        scopes: Object.keys(scopes).filter((k) => scopes[k]),
        test_only: testOnly,
      });
      onCreated(r.raw_key);
      onClose();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit}>
          <div className="modal-header">
            <div className="modal-header-text">
              <h2>New API key</h2>
              <p>Generate a fresh Bearer token for this venue.</p>
            </div>
          </div>
          <div className="modal-body">
            {err && <div className="toast toast-error" style={{ marginBottom: "1rem" }}>{err}</div>}
            <div className="form-field">
              <label>Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Kiosk #3 / Integration tests" required />
            </div>
            <div className="form-field">
              <label>Scopes</label>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                {ALL_SCOPES.map((s) => (
                  <label key={s.key} style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                    <input type="checkbox" checked={!!scopes[s.key]} onChange={(e) => setScopes({ ...scopes, [s.key]: e.target.checked })} />
                    <span>{s.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-field">
              <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                <input type="checkbox" checked={testOnly} onChange={(e) => setTestOnly(e.target.checked)} />
                <span>Test-only (forces sandbox mode regardless of venue toggle)</span>
              </label>
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy || !name}>
              {busy ? "Creating…" : "Create key"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ShowRawKeyModal({ rawKey, onClose }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-text">
            <h2>API key created</h2>
            <p>Copy it now — you won't see it again.</p>
          </div>
        </div>
        <div className="modal-body">
          <div style={{
            padding: "0.75rem 1rem",
            background: "var(--bg-elevated)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            fontFamily: "monospace",
            fontSize: "0.9rem",
            wordBreak: "break-all",
            marginBottom: "1rem",
          }}>{rawKey}</div>
          <button className="btn btn-outline" onClick={() => { navigator.clipboard.writeText(rawKey); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
            {copied ? "Copied!" : "Copy to clipboard"}
          </button>
          <div style={{ marginTop: "1rem", padding: "0.7rem 0.9rem", background: "var(--warning-soft, rgba(245,158,11,0.12))", border: "1px solid var(--warning, #d97706)", borderRadius: 6, fontSize: "0.85rem" }}>
            ⚠ This is the only time the raw key will be shown. Store it in your secret manager now.
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

/* ── API request logs viewer ─────────────────────── */
function ApiLogsList({ venueId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const r = await adminApi.listApiLogs(venueId, {
        limit: 200,
        ...(statusFilter !== "all" ? { status: statusFilter } : {}),
      });
      setLogs(r.logs || []);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, [venueId, statusFilter]);
  useEffect(() => { reload(); }, [reload]);

  async function openLog(id) {
    try { const detail = await adminApi.getApiLog(venueId, id); setSelected(detail); }
    catch (e) { setErr(e.message); }
  }

  return (
    <div className="form-section">
      <div className="section-header-row">
        <div>
          <h2>API request logs</h2>
          <p className="section-desc">Every call to /api/v1/* for this venue. Click a row for full request + response + provider trace.</p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="success">Success only</option>
            <option value="error">Errors only</option>
          </select>
          <button className="btn btn-ghost" onClick={reload}>Refresh</button>
        </div>
      </div>

      {err && <div className="toast toast-error" style={{ marginBottom: "1rem" }}>{err}</div>}
      {loading ? <div style={{ color: "var(--text-muted)" }}>Loading…</div> :
        logs.length === 0 ? (
          <div className="empty-state" style={{ padding: "2rem", textAlign: "center" }}>
            <p style={{ color: "var(--text-muted)" }}>No API requests yet.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table" style={{ fontSize: "0.82rem" }}>
              <thead>
                <tr><th>Time</th><th>Method</th><th>Path</th><th>Status</th><th>Duration</th><th>Key prefix</th><th>IP</th></tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} onClick={() => openLog(l.id)} style={{ cursor: "pointer" }}
                      className={l.status_code >= 400 ? "row-error" : ""}>
                    <td style={{ color: "var(--text-muted)", whiteSpace: "nowrap" }}>{new Date(l.created_at).toLocaleString()}</td>
                    <td><code style={{ fontWeight: 600 }}>{l.method}</code></td>
                    <td><code style={{ fontSize: "0.78rem" }}>{l.path}</code></td>
                    <td>
                      <span className={`pill ${l.status_code < 400 ? "pill-ok" : "pill-warn"}`}>{l.status_code || "-"}</span>
                    </td>
                    <td style={{ fontVariantNumeric: "tabular-nums" }}>{l.duration_ms ? `${l.duration_ms} ms` : "—"}</td>
                    <td><code style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{l.api_key_prefix || "—"}</code></td>
                    <td style={{ color: "var(--text-muted)" }}>{l.ip_address || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {selected && <LogDetailModal log={selected} onClose={() => setSelected(null)} />}
      <style>{`.row-error { background: var(--danger-soft, rgba(239,68,68,0.06)); }`}</style>
    </div>
  );
}

function LogDetailModal({ log, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 820 }}>
        <div className="modal-header">
          <div className="modal-header-text">
            <h2>{log.method} {log.path}</h2>
            <p>{new Date(log.created_at).toLocaleString()} · {log.status_code} · {log.duration_ms ?? "?"} ms</p>
          </div>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ fontSize: "0.85rem" }}>
          {log.error_message && (
            <div className="toast toast-error" style={{ marginBottom: "1rem", whiteSpace: "pre-wrap" }}>
              {log.error_message}
            </div>
          )}

          <KvBlock title="Context" rows={[
            ["Key prefix",       log.api_key_prefix],
            ["Idempotency key",  log.idempotency_key],
            ["IP",               log.ip_address],
            ["User-Agent",       log.user_agent],
            ["Test request",     log.is_test ? "Yes" : "No"],
          ]} />

          <JsonBlock title="Request body"  data={log.request_body} />
          <JsonBlock title="Response body" data={log.response_body} />
          <JsonBlock title="Provider calls" data={log.provider_calls} />
        </div>
      </div>
    </div>
  );
}

function KvBlock({ title, rows }) {
  const filtered = rows.filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (!filtered.length) return null;
  return (
    <section style={{ marginBottom: "1rem" }}>
      <h4 style={{ fontSize: "0.72rem", textTransform: "uppercase", color: "var(--text-muted)", margin: "0 0 0.4rem" }}>{title}</h4>
      <dl style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "0.25rem 0.75rem", margin: 0 }}>
        {filtered.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt style={{ color: "var(--text-muted)" }}>{k}</dt>
            <dd style={{ margin: 0 }}>{v}</dd>
          </React.Fragment>
        ))}
      </dl>
    </section>
  );
}

function JsonBlock({ title, data }) {
  if (!data) return null;
  return (
    <section style={{ marginBottom: "1rem" }}>
      <h4 style={{ fontSize: "0.72rem", textTransform: "uppercase", color: "var(--text-muted)", margin: "0 0 0.4rem" }}>{title}</h4>
      <pre style={{
        margin: 0, padding: "0.6rem 0.75rem",
        background: "var(--bg-elevated)", border: "1px solid var(--border)",
        borderRadius: 6, fontSize: "0.72rem", overflow: "auto", maxHeight: 260,
      }}>{JSON.stringify(data, null, 2)}</pre>
    </section>
  );
}
